import React, { useState } from 'react';

const SubmitGuest = () => {
  const [formData, setFormData] = useState({
    guestName: '',
    guestEmail: '',
    whyNominate: '',
    socialLinks: '',
    relationship: '',
    referrerName: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const relationshipOptions = [
    'Radio/la sam sa njima',
    'Ja sam fan',
    'Mi smo prijatelji',
    'Pratim njihov rad',
    'Čuo/la sam za njih',
    'Ostalo'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const { guestName, guestEmail, whyNominate } = formData;
    
    if (!guestName.trim()) return 'Guest name is required';
    if (!guestEmail.trim() || !guestEmail.includes('@')) return 'Valid guest email is required';
    if (!whyNominate.trim()) return 'Please tell us more about their story';
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      alert(validationError);
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'guest_suggestion',
          name: formData.guestName,
          email: formData.guestEmail,
          payload: JSON.stringify(formData)
        })
      });

      if (!response.ok) throw new Error('Submission failed');

      setIsSubmitted(true);
      setShowConfetti(true);
      
      // Hide confetti after animation
      setTimeout(() => {
        setShowConfetti(false);
      }, 3000);
    } catch (error) {
      console.error('Submission error:', error);
      alert('Failed to submit nomination. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      guestName: '',
      guestEmail: '',
      whyNominate: '',
      socialLinks: '',
      relationship: '',
      referrerName: ''
    });
    setIsSubmitted(false);
  };

  const SparkleIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 0l3.09 6.26L22 9.27l-6.91 3.01L12 24l-3.09-11.72L2 9.27l6.91-3.01L12 0z"/>
    </svg>
  );

  const TrophyIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h3a1 1 0 01.994.884L21 5v2a3 3 0 01-2.824 2.995L18 10v8a2 2 0 01-1.85 1.995L16 20H8a2 2 0 01-1.995-1.85L6 18v-8a3 3 0 01-2.995-2.824L3 7V5a1 1 0 01.883-.993L4 4h3zm11 4a1 1 0 00.993-.883L19 7V6h-2v2zm-8 12h4v-2H9v1zm1-3h4v-1h-4v1zm0-2h4V9H9v6zM5 6v1a1 1 0 00.883.993L6 8V6H5z"/>
    </svg>
  );

  const SendIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  );

  return (
    <div className="min-h-screen bg-midnight text-warm-white font-geist relative overflow-hidden">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          <div className="confetti-container">
            {[...Array(50)].map((_, i) => (
              <div key={i} className="confetti" style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                backgroundColor: Math.random() > 0.5 ? '#F9DE33' : '#134e4a'
              }} />
            ))}
          </div>
        </div>
      )}

      {!isSubmitted ? (
        <>
          {/* Hero Section */}
          <div className="pt-32 pb-16 px-6 lg:px-12">
            <div className="max-w-4xl mx-auto text-center">
              <div className="mb-8">
                <h1 className="font-clash text-5xl lg:text-7xl font-bold mb-6 tracking-tight">
                  Predloži gosta
                </h1>
                <p className="text-xl lg:text-2xl text-soft-gray leading-relaxed mb-4 font-geist">
                  Znate li nekoga sa iskrenom, snažnom pričom koja zaslužuje da bude ispričana?
                </p>
                <p className="text-lg text-electric-yellow italic font-geist">
                  Tražimo osnivače, stvaraoce, buntovnike i tihe disruptore koji imaju nešto istinito da podijele.
                </p>
              </div>

              {/* Casting Call Aesthetic */}
              <div className="bg-gradient-to-r from-deep-teal/10 to-electric-yellow/5 backdrop-blur-sm border border-electric-yellow/20 rounded-3xl p-8 mb-8">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <SparkleIcon />
                  <h2 className="font-clash text-2xl font-bold text-electric-yellow">
                    Poziv na kasting
                  </h2>
                  <SparkleIcon />
                </div>
                <p className="text-soft-gray leading-relaxed">
                  Ovo nije samo još jedan formular za zakazivanje podkasta. Mi biramo razgovore koji imaju značaj, 
                  priče koje inspirišu i glasove koje treba osloboditi okvira. Ako znate nekoga čije bi putovanje moglo promijeniti način na koji drugi razmišljaju o preduzetništvu, neuspjehu ili uspjehu — želimo da čujemo za njih.
                </p>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="px-6 lg:px-12 pb-20">
            <div className="max-w-4xl mx-auto">
              <form onSubmit={handleSubmit} className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-3xl p-8 lg:p-12">
                {/* Guest Information */}
                <div className="mb-8">
                  <h3 className="font-clash text-2xl font-bold mb-6 text-electric-yellow">
                    Informacije o gostu
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-soft-gray mb-2">
                        Puno ime gosta *
                      </label>
                      <input
                        type="text"
                        name="guestName"
                        value={formData.guestName}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                        placeholder="npr. Sarah Chen"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-soft-gray mb-2">
                        Email adresa gosta *
                      </label>
                      <input
                        type="email"
                        name="guestEmail"
                        value={formData.guestEmail}
                        onChange={handleInputChange}
                        required
                        className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                        placeholder="sarah@company.com"
                      />
                      <p className="text-xs text-soft-gray/70 mt-1">Koristi se za kontakt ukoliko odlučimo da ih predstavimo</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-soft-gray mb-2">
                      Linkovi ka društvenim mrežama ili sajtu
                    </label>
                    <input
                      type="text"
                      name="socialLinks"
                      value={formData.socialLinks}
                      onChange={handleInputChange}
                      className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                      placeholder="LinkedIn, Twitter, Website itd."
                    />
                  </div>
                </div>

                {/* The Story */}
                <div className="mb-8">
                  <h3 className="font-clash text-2xl font-bold mb-4 text-electric-yellow">
                    Zašto bi trebalo da budu na Unframed Talks? *
                  </h3>
                  <p className="text-soft-gray mb-4">
                    Šta njihovu priču čini sirovom, jedinstvenom ili uticajnom? Tražimo autentična putovanja sa stvarnim borbama i uvidima.
                  </p>
                  
                  <textarea
                    name="whyNominate"
                    value={formData.whyNominate}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                    placeholder="Ispričajte nam njihovu priču... Koje su izazove prevazišli? Koje jedinstvene uvide imaju? Šta njihovo putovanje čini vrijednim dijeljenja?"
                  />
                  
                  <div className="mt-2 text-xs text-soft-gray/70">
                    <p>💡 Ključne riječi koje volimo: burnout, pivot, neuspjeh, bootstrapped, mentalno zdravlje, izazovi skaliranja, autentično liderstvo</p>
                  </div>
                </div>

                {/* Relationship & Referrer */}
                <div className="mb-8">
                  <h3 className="font-clash text-2xl font-bold mb-6 text-electric-yellow">
                    Vaša veza
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-soft-gray mb-2">
                        Kako poznajete ovu osobu?
                      </label>
                      <select
                        name="relationship"
                        value={formData.relationship}
                        onChange={handleInputChange}
                        className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                      >
                        <option value="">Odaberite odnos</option>
                        {relationshipOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-soft-gray mb-2">
                        Vaše ime (opciono)
                      </label>
                      <input
                        type="text"
                        name="referrerName"
                        value={formData.referrerName}
                        onChange={handleInputChange}
                        className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
                        placeholder="Za transparentnost — i možda zaslugu 😉"
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-electric-yellow text-midnight px-8 py-4 rounded-xl font-bold text-lg hover:bg-electric-yellow/90 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-3"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-midnight"></div>
                        Šalje se...
                      </>
                    ) : (
                      <>
                        <SendIcon />
                        Pošalji nominaciju
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      ) : (
        /* Success State */
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-8">
              <div className="text-8xl mb-6 animate-bounce">🎬</div>
              <h1 className="font-clash text-4xl lg:text-6xl font-bold mb-6 text-electric-yellow">
                Hvala. Primili smo.
              </h1>
              <p className="text-xl lg:text-2xl text-soft-gray leading-relaxed mb-8">
                Pažljivo pregledam svakog gosta. Ako osjećamo da odgovara, javićemo se.
              </p>
            </div>

            <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-3xl p-8 mb-8">
              <h3 className="font-bold text-xl mb-4 text-electric-yellow">Šta se dešava dalje?</h3>
              <div className="text-left space-y-3 text-soft-gray">
                <p>• Pregledat ćemo priču {formData.guestName} u roku od 5 radnih dana</p>
                <p>• Ako odgovaraju, direktno ćemo ih kontaktirati</p>
                <p>• Dobićete zaslugu ako se pojave u emisiji</p>
                <p>• Pratite svoju email adresu za ažuriranja</p>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={resetForm}
                className="bg-electric-yellow text-midnight px-6 py-3 rounded-xl font-bold hover:bg-electric-yellow/90 transition-all duration-300 mr-4"
              >
                Predloži još jednog gosta
              </button>
              
              <div className="text-soft-gray">
                <p>Želite da budete u toku? Pratite nas na društvenim mrežama za sadržaj iza kulisa.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        .confetti-container {
          position: relative;
          width: 100%;
          height: 100%;
        }
        
        .confetti {
          position: absolute;
          width: 10px;
          height: 10px;
          animation: confetti-fall 3s linear infinite;
          opacity: 0.8;
        }
        
        @keyframes confetti-fall {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        .animate-bounce {
          animation: bounce 2s infinite;
        }
        
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0,0,0);
          }
          40%, 43% {
            transform: translate3d(0,-15px,0);
          }
          70% {
            transform: translate3d(0,-7px,0);
          }
          90% {
            transform: translate3d(0,-2px,0);
          }
        }
      `}</style>
    </div>
  );
};

export default SubmitGuest;